import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import ServiceDeskAPIService, {
  ptsTaskListQueryKey,
} from "@sd/services/APIs";
import { normalizeMultiFilterValues } from "@sd/lib/ticketFilterApi";
import {
  ensurePtsSelectedOption,
  ptsTaskOptionsFromPayload,
  type PtsDropdownOption,
} from "@sd/lib/ptsDropdownOptions";

/** Module filter is only valid when exactly one project is selected (backend: no comma-separated). */
export function resolvePtsModuleFilterProjectId(
  projectFilters: string[]
): string {
  const ids = normalizeMultiFilterValues(projectFilters);
  return ids.length === 1 ? ids[0]! : "";
}

export function isSingleProjectSelectedForModuleFilter(
  projectFilters: string[]
): boolean {
  return normalizeMultiFilterValues(projectFilters).length === 1;
}

export function usePtsModuleFilterOptions(
  projectFilters: string[],
  selectedModuleId?: string
): {
  showModuleFilter: boolean;
  moduleOptions: PtsDropdownOption[];
  modulesLoading: boolean;
  modulesError: boolean;
  projectId: string;
} {
  const projectId = useMemo(
    () => resolvePtsModuleFilterProjectId(projectFilters),
    [projectFilters]
  );

  const showModuleFilter = projectId.length > 0;

  const { data, isPending, isError } = useQuery({
    queryKey: ptsTaskListQueryKey(projectId),
    queryFn: async () => {
      const response = await ServiceDeskAPIService.pts.taskList(projectId);
      return response.data;
    },
    enabled: showModuleFilter,
    staleTime: 5 * 60 * 1000,
  });

  const moduleOptions = useMemo(() => {
    let options = ptsTaskOptionsFromPayload(data);
    const selected = String(selectedModuleId ?? "").trim();
    if (selected && selected !== "all") {
      options = ensurePtsSelectedOption(options, selected, "Selected module");
    }
    return options;
  }, [data, selectedModuleId]);

  return {
    showModuleFilter,
    moduleOptions,
    modulesLoading: showModuleFilter && isPending,
    modulesError: isError,
    projectId,
  };
}
